/**
 * Toolbox Profile Service (#638)
 *
 * Frontend API client for managing per-user toolbox profiles via REST API.
 * Communicates with backend toolboxProfilesRouter.ts.
 *
 * Mirrors LayerPresetService: fetch with credentials: 'include' so the FusionAuth
 * httpOnly cookie carries auth, a Bearer header only when a real token exists,
 * and fail-fast error handling (no silent fallbacks) on !response.ok or a
 * { success: false } envelope.
 */

import type {
  ToolboxProfile,
  CreateToolboxProfileInput,
  UpdateToolboxProfileInput,
} from '../types/toolboxProfile';
import { getBackendUrl, isDevMode } from './configService';

// Get backend URL from runtime config (fail-fast, no fallback)
const getApiBaseUrl = (): string => {
  const url = getBackendUrl();
  if (!url) {
    throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
  }
  return url;
};

export class ToolboxProfileService {
  private get baseUrl(): string {
    return `${getApiBaseUrl()}/api/toolbox-profiles`;
  }

  /** Get auth token from localStorage. */
  private getAuthToken(): string | null {
    const token = localStorage.getItem('access_token');

    if (!token && isDevMode()) {
      return 'dev-mode-token';
    }

    return token;
  }

  /** Build headers with authentication. */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    // Only add Authorization header if we have a REAL token. Don't send the
    // fake 'dev-mode-token' — let the cookie handle auth instead.
    if (token && token !== 'dev-mode-token') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Get all profiles for the current user (their own profiles + the global default).
   */
  async getProfilesForCurrentUser(): Promise<ToolboxProfile[]> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch toolbox profiles: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch toolbox profiles');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching toolbox profiles:', error);
      throw error;
    }
  }

  /**
   * Get a single profile by id.
   */
  async getProfileById(id: string): Promise<ToolboxProfile> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch toolbox profile: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch toolbox profile');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching toolbox profile:', error);
      throw error;
    }
  }

  /**
   * Create a new profile. The server enforces per-user unique names and returns
   * a 409 with a friendly message on a duplicate.
   */
  async createProfile(input: CreateToolboxProfileInput): Promise<ToolboxProfile> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.message || `Failed to create toolbox profile: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to create toolbox profile');
      }

      return result.data;
    } catch (error) {
      console.error('Error creating toolbox profile:', error);
      throw error;
    }
  }

  /**
   * Update an existing profile (rename and/or change the enabled/ordered tool set).
   */
  async updateProfile(id: string, input: UpdateToolboxProfileInput): Promise<ToolboxProfile> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.message || `Failed to update toolbox profile: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to update toolbox profile');
      }

      return result.data;
    } catch (error) {
      console.error('Error updating toolbox profile:', error);
      throw error;
    }
  }

  /**
   * Delete a profile.
   */
  async deleteProfile(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.message || `Failed to delete toolbox profile: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete toolbox profile');
      }

      return true;
    } catch (error) {
      console.error('Error deleting toolbox profile:', error);
      throw error;
    }
  }
}

// Export a singleton instance for convenience.
export const toolboxProfileService = new ToolboxProfileService();
